import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import {
  ChevronLeft, FileText, FileSpreadsheet, Loader2, Lock, ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { useData } from "@/contexts/DataContext";
import { effectiveTier, isPlanActive } from "@/lib/planHelpers";
import {
  aggregateGstReport,
  generateGstReportExcel,
  generateGstReportPdf,
  resolveStateFromGstin,
  type GstReportData,
} from "@/lib/gstReports";
import { useToast } from "@/hooks/use-toast";

function buildMonthOptions() {
  const now = new Date();
  const out: { label: string; value: string; from: Date; to: Date }[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      label: format(d, "MMM yyyy"),
      value: format(d, "yyyy-MM"),
      from: startOfMonth(d),
      to: endOfMonth(d),
    });
  }
  return out;
}

function buildQuarterOptions() {
  const now = new Date();
  const out: { label: string; value: string; from: Date; to: Date }[] = [];
  for (let i = 0; i < 4; i++) {
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
    const m = monthEnd.getMonth();
    const qStartMonth = m >= 3 && m <= 5 ? 3 : m >= 6 && m <= 8 ? 6 : m >= 9 && m <= 11 ? 9 : 0;
    const qStartYear = monthEnd.getFullYear();
    const qStart = new Date(qStartYear, qStartMonth, 1);
    const qEnd = endOfMonth(new Date(qStartYear, qStartMonth + 2, 1));
    const qNum = qStartMonth === 3 ? 1 : qStartMonth === 6 ? 2 : qStartMonth === 9 ? 3 : 4;
    const fyStart = qStartMonth >= 3 ? qStartYear : qStartYear - 1;
    const value = `${fyStart}-Q${qNum}`;
    if (!out.find((x) => x.value === value)) {
      out.push({
        label: `Q${qNum} FY${String(fyStart).slice(-2)}-${String(fyStart + 1).slice(-2)}`,
        value,
        from: qStart,
        to: qEnd,
      });
    }
  }
  return out;
}

export default function GstReports() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { transactions, products, businessProfile, plan, saveReportRecord } = useData() as any;

  const [periodKind, setPeriodKind] = useState<"month" | "quarter">("month");
  const monthOptions = useMemo(buildMonthOptions, []);
  const quarterOptions = useMemo(buildQuarterOptions, []);
  const [selectedValue, setSelectedValue] = useState<string>(monthOptions[0]?.value ?? "");
  const [busy, setBusy] = useState<"PDF" | "EXCEL" | null>(null);

  const tier = effectiveTier(plan);
  const planActive = isPlanActive(plan);
  const canUseGstReports = (tier === "pro" || tier === "ultimate") && planActive;
  const stateFromGstin = useMemo(
    () => resolveStateFromGstin(businessProfile?.gstin),
    [businessProfile?.gstin],
  );

  const opts = periodKind === "month" ? monthOptions : quarterOptions;
  const periodSelected = useMemo(
    () => opts.find((o) => o.value === selectedValue) || opts[0],
    [opts, selectedValue],
  );

  const handleBack = () => {
    if (window.history.length > 1) window.history.back();
    else setLocation("/settings");
  };

  const generate = async (formatType: "PDF" | "EXCEL") => {
    if (!canUseGstReports) {
      toast({ title: "Premium required", description: "Upgrade to Premium to file GST reports.", variant: "destructive" });
      return;
    }
    if (!stateFromGstin) {
      toast({
        title: "GSTIN missing or invalid",
        description: "Set a valid 15-character GSTIN in Business Profile before generating reports.",
        variant: "destructive",
      });
      return;
    }
    if (!periodSelected) return;

    setBusy(formatType);
    try {
      // Pre-load both logos in parallel — jsPDF needs HTMLImageElement to rasterise.
      const loadImage = (src: string): Promise<HTMLImageElement | null> =>
        new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          img.src = src;
        });
      const [businessLogoImg, dhLogoImg] = await Promise.all([
        businessProfile?.businessLogo ? loadImage(businessProfile.businessLogo) : Promise.resolve(null),
        loadImage("/DailyHisaab-logo-no-outline.png"),
      ]);

      const data: GstReportData = {
        transactions: transactions || [],
        startDate: periodSelected.from,
        endDate: periodSelected.to,
        businessName: businessProfile?.businessName || "DailyHisaab Business",
        businessAddress: businessProfile?.businessAddress || "",
        businessGstin: (businessProfile?.gstin || "").toUpperCase(),
        businessStateCode: stateFromGstin.code,
        businessStateName: stateFromGstin.name,
        periodLabel: periodSelected.value,
        products: products || [],
        businessPhone: businessProfile?.businessPhone || "",
        businessEmail: businessProfile?.businessEmail || "",
        businessLogo: businessLogoImg,
        dhLogo: dhLogoImg,
      };

      if (formatType === "PDF") await generateGstReportPdf(data);
      else await generateGstReportExcel(data);

      const agg = aggregateGstReport(data);
      await saveReportRecord({
        reportType: "GSTR-1+3B",
        format: formatType,
        startDate: format(data.startDate, "yyyy-MM-dd"),
        endDate: format(data.endDate, "yyyy-MM-dd"),
        rangeLabel: `GST ${data.periodLabel}`,
        createdAt: new Date().toISOString(),
        meta: {
          taxableValue: agg.gstr3b.taxableValue,
          outputTaxLiability: agg.gstr3b.outputTaxLiability,
          billCount: agg.documentsIssued.totalNumber,
          cancelledCount: agg.documentsIssued.cancelled,
        },
      });

      toast({ title: `GST Report (${formatType}) saved to Documents` });
    } catch (err: any) {
      console.error("[GstReports] generation failed:", err);
      toast({
        title: "Could not generate report",
        description: err?.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  };

  if (!canUseGstReports) {
    return (
      <div className="min-h-screen bg-[#FFFBEB] pb-12">
        <header className="bg-[#991B1B] sticky top-0 z-10 shadow-md">
          <div className="max-w-xl mx-auto px-4 h-16 flex items-center gap-3">
            <button onClick={handleBack} className="text-white p-2 rounded-full active:bg-white/10">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-white font-bold text-lg">GST Filing</span>
          </div>
        </header>
        <main className="max-w-xl mx-auto p-4 mt-6">
          <div className="bg-white rounded-3xl p-8 text-center space-y-5 shadow-sm border border-slate-100">
            <div className="w-20 h-20 mx-auto rounded-3xl bg-amber-50 flex items-center justify-center">
              <Lock className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-black uppercase tracking-tighter text-[#0F172A]">
              GST Filing — <span className="text-[#8B1A1A]">Premium feature</span>
            </h2>
            <p className="text-slate-500 text-sm">
              Auto-generate CA-ready GSTR-1 (B2CS, HSN Summary, Nil-Rated, Documents Issued) and GSTR-3B summary in Excel + PDF for any month or quarter. Available on Premium and Ultimate plans.
            </p>
            <button
              onClick={() => setLocation("/subscription")}
              className="h-12 px-6 rounded-full bg-[#8B1A1A] active:bg-[#631212] text-white font-black uppercase tracking-widest text-xs"
            >
              Go Premium
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBEB] pb-24">
      <header className="bg-[#991B1B] sticky top-0 z-10 shadow-md">
        <div className="max-w-xl mx-auto px-4 h-16 flex items-center gap-3">
          <button onClick={handleBack} className="text-white p-2 rounded-full active:bg-white/10">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-white font-bold text-lg">GST Filing</span>
        </div>
      </header>

      <main className="max-w-xl mx-auto p-4 space-y-5">
        <div className="space-y-2 pt-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-black uppercase tracking-widest">
            <ShieldCheck className="w-3 h-3" /> GSTR-1 + GSTR-3B
          </div>
          <h1 className="text-3xl font-black text-[#0F172A] tracking-tighter uppercase leading-tight">
            File GST <br /><span className="text-[#8B1A1A]">in one click</span>
          </h1>
          <p className="text-slate-500 text-sm">
            Excel + PDF, ready to hand to your CA.
          </p>
        </div>

        {!stateFromGstin && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-bold text-amber-900">Set a valid GSTIN first</p>
              <p className="text-xs text-amber-700">
                Your business GSTIN ({businessProfile?.gstin || "(empty)"}) doesn't match the standard 15-character format. The state of supply for your B2CS section is derived from it.
              </p>
              <button
                onClick={() => setLocation("/profile")}
                className="mt-2 h-9 px-3 rounded-lg text-[11px] font-black uppercase tracking-widest bg-white border border-amber-300 text-amber-900 active:bg-amber-100"
              >
                Open Profile
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl p-5 space-y-5 shadow-sm border border-slate-100">
          <div className="flex gap-2">
            {(["month", "quarter"] as const).map((k) => (
              <button
                key={k}
                onClick={() => {
                  setPeriodKind(k);
                  setSelectedValue((k === "month" ? monthOptions : quarterOptions)[0]?.value ?? "");
                }}
                className={`flex-1 h-10 rounded-full text-[11px] font-black uppercase tracking-widest transition-all ${
                  periodKind === k
                    ? "bg-[#8B1A1A] text-white shadow-md"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {k === "month" ? "Monthly" : "Quarterly"}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            {opts.map((o) => (
              <button
                key={o.value}
                onClick={() => setSelectedValue(o.value)}
                className={`p-3 rounded-2xl text-left text-[11px] font-bold transition-all border ${
                  o.value === selectedValue
                    ? "bg-[#8B1A1A]/5 border-[#8B1A1A] text-[#8B1A1A]"
                    : "bg-white border-slate-100 text-slate-500"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div className="space-y-3 pt-2">
            <button
              onClick={() => generate("EXCEL")}
              disabled={busy !== null || !stateFromGstin}
              className="w-full h-14 rounded-full bg-emerald-600 active:bg-emerald-700 text-white font-black uppercase tracking-widest text-xs shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy === "EXCEL" ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileSpreadsheet className="w-5 h-5" />}
              {busy === "EXCEL" ? "Generating..." : "Download Excel"}
            </button>
            <button
              onClick={() => generate("PDF")}
              disabled={busy !== null || !stateFromGstin}
              className="w-full h-14 rounded-full bg-[#8B1A1A] active:bg-[#631212] text-white font-black uppercase tracking-widest text-xs shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy === "PDF" ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
              {busy === "PDF" ? "Generating..." : "Download PDF"}
            </button>
          </div>
        </div>

        <div className="bg-slate-50 rounded-2xl p-4 space-y-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Covered</p>
          <ul className="text-xs text-slate-600 space-y-1 pl-4 list-disc">
            <li><strong>GSTR-1:</strong> B2CS, HSN Summary, Nil-Rated, Documents Issued.</li>
            <li><strong>GSTR-3B:</strong> 3.1(a), 3.1(c), output tax liability.</li>
          </ul>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 pt-2">Not covered (file manually on the portal)</p>
          <ul className="text-xs text-slate-600 space-y-1 pl-4 list-disc">
            <li>B2B invoices, B2C Large &gt;₹2.5L, exports, e-commerce.</li>
            <li>Credit/Debit Notes, refunds.</li>
            <li>GSTR-3B Table 4 ITC.</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
